/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useMemo, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Field, Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { useDispatch } from 'react-redux';
import { ActionButton, BrandColors, Modal, Header, LinkButton } from '@blend/enterprise-components';
import { colors, fonts, mixins } from '@blend/consumer-components';
import { FormApi } from 'final-form';
import { setRoomError } from '../../../../../actions/roomActions';
import { useParticipants } from '../../../../../hooks/useParticipants';
import { notaryAPI } from '../../../../../api/notary';
import {
  getEmailInputConfig,
  phoneNumberInputConfig,
  getTitleInputConfig,
  secondaryTitleInputConfig,
  getNameInputConfig,
  isKBARequiredInputConfig,
  getSignatureInputConfig,
  getSecondarySignatureInputConfig,
} from './inputConfigs';
import { getConfig } from '@utils/configs';
import { ModalContent } from '@components/common/enterprise/ModalContent/ModalContent';
import { WrappedInput } from '@components/common/enterprise/WrapperInput/WrapperInput';
import { formatFullName } from '@utils/formatting';

const styles = StyleSheet.create({
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 14,
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
  },
  message: {
    color: colors.catalystLight90,
    marginTop: 12,
    marginBottom: 12,
    fontSize: '.75rem',
    lineHeight: '21px',
    letterSpacing: `0.03em`,
    ...fonts.Roboto,
  },
  rowSingle: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
    display: 'flex',
  },
  rowRolesAndSignatures: {
    borderBottom: `1px solid ${BrandColors.CLOUD_70}`,
    marginBottom: 16,
    paddingBottom: 16,
  },
  sectionHeader: {
    marginBottom: 16,
  },
  submitWrapper: {
    marginLeft: 8,
  },
  addButton: {
    ...mixins.marginHorizontal(10),
    fontSize: 14,
    cursor: 'pointer',
    color: BrandColors.PRIMARY_500,
    // highlight background when keyboard tabbing
    ':focus-visible': {
      outline: 'none',
      backgroundColor: BrandColors.NEUTRAL_100,
    },
  },
  removeButton: {
    cursor: 'pointer',
    color: BrandColors.NEUTRAL_500,
    fontSize: 14,
    ':focus-visible': {
      outline: 'none',
      backgroundColor: BrandColors.NEUTRAL_100,
    },
  },
});

export const idvRequiredMessage = 'This participant must verify their ID.';
export const kbaRequiredMessage = 'This participant must answer security questions.';

export namespace ParticipantModal {
  export interface Props {
    isOpen: boolean;
    roomId: string;
    headerText: string;
    subHeaderText?: string;
    initialValue?: ParticipantModalFormValues;
    handleClose: () => void;
    handleFormSubmit: (formValues: ParticipantModalFormValues) => Promise<void>;
    isGenericParticipant: boolean;
  }
}

export interface ParticipantAdditionalTitles {
  id?: string;
  role: string;
  signature: string;
}

export interface ParticipantModalFormValues {
  title?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  suffix?: string | null;
  email?: string;
  phoneNumber?: string | null;
  requiresKba?: boolean;
  signature?: string;
  rolesAndSignatures?: ParticipantAdditionalTitles[];
}

type Name = 'first' | 'middle' | 'last' | 'suffix';

const generateSignature = (
  enteredValue: string,
  enteredNameType: Name,
  nameValues: ParticipantModalFormValues,
): string => {
  switch (enteredNameType) {
    case 'first':
      return formatFullName({ ...nameValues, firstName: enteredValue.trim() });
    case 'middle':
      return formatFullName({ ...nameValues, middleName: enteredValue.trim() });
    case 'last':
      return formatFullName({ ...nameValues, lastName: enteredValue.trim() });
    case 'suffix':
      return formatFullName({ ...nameValues, suffix: enteredValue.trim() });
    default:
      return '';
  }
};

/** Return first name and last name form field. When user types on these fields, it generates data in field Signature automatically */
const getNameField = (
  form: FormApi<ParticipantModalFormValues>,
  values: ParticipantModalFormValues,
  type: Name,
  disabled = false,
) => {
  const { validate, component, ...config }: Omit<WrappedInput.Props, 'input' | 'meta'> = getNameInputConfig(type);
  return (
    <Field name={config.name} validate={validate}>
      {({ input, meta }) => {
        const captureOnchange = (e: any) => {
          input.onChange(e);
          const value = e.target.value;
          // concat all parts of name to generate the signature data field
          const signature = generateSignature(value, type, values);
          form.change('signature', signature.trim());
        };
        return (
          <WrappedInput
            label={config.label}
            inputComponent={config.inputComponent}
            input={input}
            meta={meta}
            {...config}
            customComponentProps={{ ...config.customComponentProps, disabled }}
            labelSize={config.labelSize}
            onChange={captureOnchange}
          />
        );
      }}
    </Field>
  );
};

const getInitialFormValues = (initialValue: ParticipantModalFormValues | undefined) => {
  // All participants require KBA
  const initialFormValues: ParticipantModalFormValues = {
    requiresKba: true,
    ...initialValue,
  };
  // Add a default signature if there is none
  if (initialValue !== undefined && !initialValue.signature) {
    initialFormValues.signature = formatFullName(initialValue);
  }
  return initialFormValues;
};

export const ParticipantModal: React.FC<ParticipantModal.Props> = ({
  isOpen,
  roomId,
  headerText,
  subHeaderText,
  initialValue,
  handleFormSubmit,
  handleClose,
  isGenericParticipant,
}) => {
  const dispatch = useDispatch();
  const { dispatchParticipants } = useParticipants(roomId);
  const isSingleOnboardingEnabled = getConfig().isSingleOnboardingEnabled;
  const [deleteRoleIds, setDeleteRoleIds] = useState<string[]>([]);
  const initialValues = useMemo(() => getInitialFormValues(initialValue), [initialValue]);

  const onSubmit = async (formValues: ParticipantModalFormValues) => {
    try {
      let values: ParticipantModalFormValues;
      Object.keys(initialValues).map(key => {
        if (!formValues[key as keyof ParticipantModalFormValues]) {
          formValues = {
            [key]: null,
            ...formValues,
          };
        }
      });
      if (isGenericParticipant) {
        values = formValues;
      } else {
        values = {
          signature: formValues.signature,
        };
        // TODO Remove this conditional and place rolesAndSignatures
        //  in values initialization once isSingleOnboardingEnabled
        //  feature flag is removed and single onboarding is permanent.
        if (formValues.rolesAndSignatures !== undefined) {
          values.rolesAndSignatures = formValues.rolesAndSignatures;
        }
      }
      await handleFormSubmit(values);
      await Promise.all(deleteRoleIds.map(roleId => notaryAPI.rooms.deleteParticipant(roomId, roleId)));
      await dispatchParticipants();
    } catch (error) {
      setRoomError({ hasError: true })(dispatch);
    } finally {
      handleSingleOnboardingClose();
    }
  };

  const handleSingleOnboardingClose = () => {
    setDeleteRoleIds([]);
    handleClose();
  };

  return (
    <Modal
      data-it="ParticipantModal"
      contentLabel={headerText}
      handleClose={handleSingleOnboardingClose}
      isOpen={isOpen}
    >
      <ModalContent handleClose={handleSingleOnboardingClose} headerText={headerText} subHeaderText={subHeaderText}>
        <Form
          mutators={{
            ...arrayMutators,
          }}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({
            handleSubmit,
            submitting,
            form: {
              mutators: { push },
            },
            form,
            values,
          }) => {
            return (
              <div>
                <div className={css(styles.sectionHeader)}>
                  <Header size={5}>Participant information</Header>
                </div>

                <div className={css(styles.rowSingle)}>
                  {getNameField(form, values, 'first', !isGenericParticipant)}
                </div>
                <div className={css(styles.rowSingle)}>
                  {getNameField(form, values, 'middle', !isGenericParticipant)}
                </div>
                <div className={css(styles.rowSingle)}>{getNameField(form, values, 'last', !isGenericParticipant)}</div>
                <div className={css(styles.rowSingle)}>
                  {getNameField(form, values, 'suffix', !isGenericParticipant)}
                </div>

                <div className={css(styles.rowSingle)}>
                  <Field {...getEmailInputConfig(!isGenericParticipant)} />
                </div>
                <div className={css(styles.rowSingle)}>
                  <Field {...phoneNumberInputConfig} disabled={!isGenericParticipant} />
                </div>

                <div className={css(styles.sectionHeader)}>
                  <Header size={5}>Role Information</Header>
                </div>

                <section className={css(styles.rowRolesAndSignatures)}>
                  <div className={css(styles.rowSingle)}>
                    <Field {...getTitleInputConfig(!isGenericParticipant)} />
                  </div>
                  <div className={css(styles.rowSingle)}>
                    <Field {...getSignatureInputConfig()} />
                  </div>
                </section>

                {isSingleOnboardingEnabled && (
                  <>
                    <FieldArray name="rolesAndSignatures">
                      {({ fields }) =>
                        fields.map((roleAndSign, index) => (
                          <div key={roleAndSign}>
                            <div className={css(styles.rowSingle)}>
                              <Field
                                {...secondaryTitleInputConfig}
                                label={`Role ${index + 2}`}
                                name={`${roleAndSign}.role`}
                              />
                            </div>
                            <div className={css(styles.rowRolesAndSignatures)}>
                              <Field
                                {...getSecondarySignatureInputConfig()}
                                label={`Signature ${index + 2}`}
                                name={`${roleAndSign}.signature`}
                              />
                              <LinkButton
                                label="Delete"
                                onClick={() => {
                                  const removed = fields.remove(index);
                                  removed.id && setDeleteRoleIds([...deleteRoleIds, removed.id]);
                                }}
                                data-it={`delete-role-${index + 2}`}
                                icon="clear"
                                iconPosition="left"
                                theme="inherit"
                                className={css(styles.removeButton)}
                              />
                            </div>
                          </div>
                        ))
                      }
                    </FieldArray>
                    <div className={css(styles.rowSingle)}>
                      <LinkButton
                        label="Add additional role and signature"
                        onClick={() => push('rolesAndSignatures', { role: '', signature: '' })}
                        data-it="add-additional-roles"
                        icon="add"
                        iconPosition="left"
                        theme="inherit"
                        className={css(styles.addButton)}
                      />
                    </div>
                  </>
                )}

                <div className={css(styles.sectionHeader)}>
                  <Header size={5}>Identity validation</Header>
                </div>
                <p className={css(styles.message)} data-it="idv-notification">
                  {idvRequiredMessage}
                </p>
                {isGenericParticipant ? (
                  <div className={css(styles.rowSingle)}>
                    <Field {...isKBARequiredInputConfig} data-it="kbaRequiredInput" disabled={!isGenericParticipant} />
                  </div>
                ) : (
                  <p className={css(styles.message)} data-it="kba-notification">
                    {kbaRequiredMessage}
                  </p>
                )}

                <div className={css(styles.footerBar)}>
                  <ActionButton
                    data-dd-privacy="allow"
                    data-it="ParticipantModal-cancel"
                    label="Cancel"
                    onClick={handleSingleOnboardingClose}
                    theme="secondary"
                  />
                  <div className={css(styles.submitWrapper)}>
                    <ActionButton
                      data-dd-privacy="allow"
                      disabled={submitting}
                      data-it="ParticipantModal-submit"
                      label={isGenericParticipant ? 'Save and send email' : 'Save'}
                      onClick={handleSubmit}
                    />
                  </div>
                </div>
              </div>
            );
          }}
        </Form>
      </ModalContent>
    </Modal>
  );
};
